import { Download, Github, Linkedin, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import { istClock, journeyUptime, useNow } from "@/hooks/useNow";
import { useVisitor } from "@/context/VisitorContext";
import { downloadResume } from "@/utils/download";
import { IS_MAC } from "@/components/desktop/Spotlight";

export default function MenuBar({ activeTitle }) {
  const now = useNow();
  const { time, date } = istClock(now);
  const uptime = journeyUptime(now);
  const { name, setPromptOpen } = useVisitor();

  return (
    <header
      className="fixed inset-x-0 top-0 z-[900] flex h-9 items-center justify-between gap-3 overflow-hidden border-b border-white/10 bg-[#0d1220]/75 px-3 backdrop-blur-2xl sm:px-5"
      data-testid="menu-bar"
    >
      <div className="flex min-w-0 items-center gap-3">
        <img src="/favicon.svg" alt="anish.dev" className="h-4 w-4" />
        <span className="font-display text-[13px] font-bold tracking-tight text-slate-100">anish.dev</span>
        {activeTitle ? (
          <span className="hidden truncate font-mono text-[11px] text-slate-500 md:block" data-testid="menu-bar-active-title">
            {activeTitle}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("spotlight:toggle"))}
          data-testid="menu-bar-spotlight"
          aria-label="Open spotlight search"
          title="Search (⌘K / Ctrl K)"
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] text-slate-300 transition-colors duration-150 hover:bg-white/10"
        >
          <Search size={11} />
          <span className="hidden font-mono text-[10px] text-slate-400 sm:inline">{IS_MAC ? "⌘K" : "Ctrl K"}</span>
        </button>
        <span className="hidden whitespace-nowrap font-mono text-[11px] text-slate-500 lg:block" data-testid="menu-bar-uptime" title="time coding since Sep 2024">
          ▲ {uptime}
        </span>
        <button
          onClick={() => setPromptOpen(true)}
          data-testid="menu-bar-greeting"
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] text-slate-300 transition-colors duration-150 hover:bg-white/10 sm:px-2.5"
        >
          <UserRound size={11} className="shrink-0" />
          <span className="max-w-[92px] truncate sm:max-w-[140px]">{name ? `hi, ${name}` : "say hi"}</span>
        </button>
        <span className="shrink-0 whitespace-nowrap font-mono text-[11px] tabular-nums text-slate-300" data-testid="menu-bar-clock">
          {time} IST<span className="hidden sm:inline"> · {date}</span>
        </span>
        <span className="hidden h-4 w-px bg-white/10 sm:block" />
        <div className="hidden items-center gap-2.5 sm:flex">
          <a href="https://github.com/anishravi" target="_blank" rel="noopener noreferrer" aria-label="GitHub profile" data-testid="menu-bar-github" className="text-slate-400 transition-colors duration-150 hover:text-white">
            <Github size={14} />
          </a>
          <a href="https://linkedin.com/in/anishravi-swe" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile" data-testid="menu-bar-linkedin" className="text-slate-400 transition-colors duration-150 hover:text-white">
            <Linkedin size={14} />
          </a>
          <button
            onClick={() => {
              downloadResume();
              toast("Resume downloading — AnishResume_WA.pdf");
            }}
            aria-label="Download resume"
            data-testid="menu-bar-resume"
            className="text-slate-400 transition-colors duration-150 hover:text-white"
          >
            <Download size={14} />
          </button>
        </div>
      </div>
    </header>
  );
}
